import { computed, watch, type Ref } from "vue";
import api from "../api";
import { useAuth } from "./use-auth";
import { createOrgCache } from "./org-cache";
import { ApiErrorCode, isApiResponseError, type Org } from "tf-api-client";

// Subscriptions, shared across all instances and keyed by organization.
const cache = createOrgCache<Org.Subscription>(async (orgId) => {
  try {
    return await api.org.getOrgSubscription(orgId);
  } catch (err) {
    // 404 `resource_not_found`: the organization simply has no subscription
    if (!(isApiResponseError(err) && err.is(ApiErrorCode.ResourceNotFound))) {
      console.error("Failed to fetch organization subscription", err);
    }
    throw err;
  }
});

// Called after a token refresh, a membership switch, a reactivation, an
// allocation change, a checkout or an upgrade, so data fetched before is not
// shown as current. The subscriptions on screen are refetched (they keep
// showing the previous value meanwhile); the others are dropped.
export function invalidateOrgSubscription(
  orgId?: string,
  options?: { refetch?: boolean },
) {
  cache.invalidate(orgId, options);
}

export function useOrgSubscription(orgIdParam?: string | Ref<string>) {
  const { selectedMembership } = useAuth();

  const orgId = computed(() => {
    if (!orgIdParam) return selectedMembership.value?.organizationId ?? "";
    if (typeof orgIdParam === "string") return orgIdParam;
    return (orgIdParam as Ref<string>).value;
  });

  cache.retain(orgId);

  const entry = computed(() => cache.entryFor(orgId.value || "default"));

  const subscription = computed(() => entry.value.data.value);
  const loading = computed(() => entry.value.loading.value);
  const notFound = computed(() => {
    const err = entry.value.error.value;
    return isApiResponseError(err) && err.is(ApiErrorCode.ResourceNotFound);
  });
  const error = computed(() =>
    entry.value.error.value && !notFound.value
      ? "Failed to fetch subscription"
      : null,
  );

  const fetchSubscription = async () => {
    if (!orgId.value) return;
    await cache.fetch(orgId.value);
  };

  // initial fetch
  fetchSubscription();

  watch(orgId, () => {
    fetchSubscription();
  });

  return {
    subscription,
    loading,
    error,
    notFound,
    fetchSubscription,
  };
}
